import React, { useState } from 'react';
import { Checkbox, IconButton, MenuItem, TextField, Tooltip } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Link } from 'react-router-dom';
import moment from 'moment';

import { getHeaderCells } from './TableHeader/getHeaderCells';
import { studentCardPath } from '../../../utils/consts/educationType';
import { listItemStyle, textFieldStyle } from '../../../utils/consts/styles';

const formatDate = (value) => (value === null ? 'Не определена' : moment(value).format('DD.MM.YYYY'));

const Field = ({ label, value }) => (
  value === null || value === undefined || value === '' ? null : (
    <div className="student-card__field">
      <span className="student-card__label">{label}</span>
      <span>{value}</span>
    </div>
  )
);

/**
 * Карточка слушателя. Набор полей тот же, что в строке таблицы у этой роли:
 * у «Преподавателя» карточки контингента нет, поэтому вместо ссылки — раскрытие
 * с почтой и телефоном, как в таблице.
 */
const StudentCard = ({ row, isTeacher, checked, onToggle }) => {
  const [open, setOpen] = useState(false);

  if (isTeacher) {
    return (
      <div className="student-card">
        <div className="student-card__head">
          <div className="student-card__title">
            {row.russian_name || row.latin_name}
            {row.russian_name && row.latin_name && (
              <div className="student-card__subtitle">{row.latin_name}</div>
            )}
          </div>
          <IconButton size="small" aria-label="expand row" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </div>
        <div className="student-card__fields">
          <Field label="Регистрация на ПОдИУ" value={formatDate(row.registered_in_system)} />
          <Field label="Кол-во часов" value={row.hours_number} />
          <Field label="Пол" value={row.gender} />
          <Field label="Тип обучения" value={row.education_type} />
          <Field label="Страна" value={row.country} />
          <Field label="Форма обучения" value={row.form_study} />
        </div>
        {open && (
          <div className="student-card__fields student-card__extra">
            <Field label="Первая эл. почта" value={row.first_student_email || '—'} />
            <Field label="Вторая эл. почта" value={row.second_student_email || '—'} />
            <Field label="Телефон" value={row.contact_phone_number || '—'} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`student-card${checked ? ' student-card--selected' : ''}`}>
      <div className="student-card__head">
        <Checkbox
          color="primary" checked={checked} onChange={onToggle}
          sx={{ p: 0.5, ml: -0.5, mt: -0.5 }}
        />
        {/* Как и в таблице, карточка открывается в новой вкладке: список с фильтрами
            остаётся на месте, и к нему не приходится возвращаться заново. */}
        <Link className="student-card__link" target="_blank" to={studentCardPath(row.education_type, row.id)}>
          <div className="student-card__title">
            {row.latin_name || row.russian_name}
            {row.latin_name && row.russian_name && (
              <div className="student-card__subtitle">{row.russian_name}</div>
            )}
          </div>
          <div className="student-card__fields">
            <Field label="Тип обучения" value={row.education_type} />
            <Field label="Кол-во часов" value={row.hours_number} />
            <Field label="Страна" value={row.country} />
            <Field label="Пол" value={row.gender} />
            <Field label="№ договора" value={row.contract_number} />
            <Field label="Статус оплаты" value={row.payment_status} />
            <Field label="№ о зачислении" value={row.enrollment_order} />
            <Field label="Зачисление" value={row.enrollment} />
            <Field label="Дата создания" value={formatDate(row.date_creation)} />
          </div>
        </Link>
      </div>
    </div>
  );
};

/**
 * Главная таблица на телефоне — списком карточек.
 *
 * В таблице 11 колонок: на узком экране видно две-три, остальное — горизонтальной
 * прокруткой без закреплённого ФИО. Всё, что умеет таблица, здесь сохранено:
 * выбор строк (для рассылки, выгрузки и правки), «выбрать все» и сортировка
 * по тем же полям, что в заголовках колонок.
 */
const MobileStudentList = ({
  rows, isTeacher, isSelected, onToggle, numSelected, rowCount, onSelectAllClick,
  order, orderBy, onSort,
}) => {
  const headCells = getHeaderCells();
  const sortValue = headCells.some((cell) => cell.id === orderBy) ? orderBy : '';

  return (
    <div>
      <div className="student-cards__controls">
        {!isTeacher && (
          <label className="student-cards__select-all">
            <Checkbox
              color="primary" sx={{ p: 0.5 }}
              indeterminate={numSelected > 0 && numSelected < rowCount}
              checked={rowCount > 0 && numSelected === rowCount}
              onChange={onSelectAllClick}
            />
            Выбрать все ({rowCount})
          </label>
        )}

        <div className="student-cards__sort">
          <TextField
            select size="small" color="warning" label="Сортировка"
            value={sortValue} fullWidth
            InputLabelProps={textFieldStyle}
            onChange={(event) => event.target.value && onSort(event.target.value, order)}
          >
            <MenuItem value="" disabled><span style={listItemStyle}>Без сортировки</span></MenuItem>
            {headCells.map((cell) => (
              <MenuItem key={cell.id} value={cell.id}>
                <span style={listItemStyle}>{cell.label}</span>
              </MenuItem>
            ))}
          </TextField>
          <Tooltip title={order === 'asc' ? 'По возрастанию' : 'По убыванию'}>
            <span>
              <IconButton
                disabled={!sortValue}
                onClick={() => onSort(sortValue, order === 'asc' ? 'desc' : 'asc')}
              >
                {order === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="student-cards__empty">Ничего не найдено</div>
      ) : (
        <div className="student-cards">
          {rows.map((row) => (
            <StudentCard
              key={row.id} row={row} isTeacher={isTeacher}
              checked={isSelected(row.id)}
              onToggle={() => onToggle(row)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MobileStudentList;
